import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Panel, Loading, ErrorNote, Empty, formatDate } from '../components/ui';
import { useRoute } from '../lib/router';

interface DocumentRow { id: string; title: string; item_count: string; ingest_status: string }

interface AskResponse {
  threadId: string;
  body: string;
  citations: { blockIndex: number; pageNumber: number; clauseRef: string | null; citedText: string }[];
  refused: boolean;
  retrieved: { blockIndex: number; pageNumber: number; clauseRef: string | null; via: string }[];
  notice: string;
}

interface Turn { question: string; answer: AskResponse | null; error?: unknown }

const EXAMPLES = [
  'What are the reporting deadlines in this contract?',
  'What does the payment depend on?',
  'Which patients are in scope?',
  'What happens if we miss a target?',
];

interface Thread {
  id: string; title: string; created_at: string;
  document_id: string | null; document_title: string | null; message_count: string;
}

interface StoredMessage {
  id: string; role: 'user' | 'assistant'; body: string;
  citations: AskResponse['citations']; refused: boolean; created_at: string;
}

export function Ask() {
  const route = useRoute();
  const [question, setQuestion] = useState(route.params.get('q') ?? '');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [threadId, setThreadId] = useState<string | undefined>();
  const [documentId, setDocumentId] = useState<string>(route.params.get('doc') ?? '');

  // Threads were being written to the database and never read back, so every
  // answer vanished on reload — including the citations someone may have been
  // relying on.
  const threads = useQuery({
    queryKey: ['ask-threads'],
    queryFn: () => api.get<{ threads: Thread[] }>('/api/ask/threads'),
  });

  const openThread = async (thread: Thread) => {
    const { messages } = await api.get<{ messages: StoredMessage[] }>(
      `/api/ask/threads/${thread.id}`,
    );
    const restored: Turn[] = [];
    for (const m of messages) {
      if (m.role === 'user') {
        restored.push({ question: m.body, answer: null });
      } else {
        const last = restored[restored.length - 1];
        const answer = {
          threadId: thread.id, body: m.body, citations: m.citations,
          refused: m.refused, retrieved: [], notice: '',
        };
        if (last && last.answer === null) last.answer = answer;
        else restored.push({ question: '', answer });
      }
    }
    setThreadId(thread.id);
    setTurns(restored);
    if (thread.document_id) setDocumentId(thread.document_id);
  };

  const documents = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.get<{ documents: DocumentRow[] }>('/api/documents'),
  });

  const ready = (documents.data?.documents ?? []).filter((d) => d.ingest_status === 'ready');
  const activeDocument = documentId || ready[0]?.id || '';

  const askMutation = useMutation({
    mutationFn: (q: string) =>
      api.post<AskResponse>('/api/ask', { documentId: activeDocument, question: q, threadId }),
    onSuccess: (data, q) => {
      setThreadId(data.threadId);
      setTurns((t) => [...t, { question: q, answer: data }]);
      setQuestion('');
      threads.refetch();
    },
    onError: (error, q) => {
      setTurns((t) => [...t, { question: q, answer: null, error }]);
    },
  });

  const submit = (q: string) => {
    if (!q.trim() || !activeDocument) return;
    askMutation.mutate(q.trim());
  };

  return (
    <>
      <PageHeader
        title="Ask InBrief"
        description="Ask a question about a contract you have uploaded. Every answer quotes the passage it came from, and InBrief says so plainly when the contract does not cover something."
      />

      <div className="note-box">
        InBrief cannot see patient records, appointment systems or clinical data. It can only answer
        from the contracts you have uploaded, so it cannot tell you which of your patients are
        outstanding on anything.
      </div>

      {documents.isLoading ? <Loading what="Loading contracts" /> : null}
      {!documents.isLoading && ready.length === 0 ? (
        <Empty>No contracts are ready yet. Upload one on the Documents page.</Empty>
      ) : null}

      {ready.length > 0 ? (
        <Panel title="Your question" icon="?">
          <div className="field">
            <label className="field-label" htmlFor="askDoc">Contract</label>
            <select
              id="askDoc"
              value={activeDocument}
              onChange={(e) => { setDocumentId(e.target.value); setThreadId(undefined); setTurns([]); }}
            >
              {ready.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>

          <form
            className="ask-form"
            onSubmit={(e) => { e.preventDefault(); submit(question); }}
          >
            <div className="field">
              <label className="field-label" htmlFor="askQuestion">Question</label>
              <input
                id="askQuestion"
                type="text"
                value={question}
                placeholder="What are the reporting deadlines?"
                onChange={(e) => setQuestion(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={askMutation.isPending || !question.trim()}>
              {askMutation.isPending ? 'Reading the contract…' : 'Ask'}
            </button>
          </form>

          <div className="example-qs">
            {EXAMPLES.map((q) => (
              <button key={q} className="example-q" onClick={() => { setQuestion(q); submit(q); }}>
                {q}
              </button>
            ))}
          </div>
        </Panel>
      ) : null}

      {threads.data?.threads.length ? (
        <Panel
          title="Earlier questions"
          icon="≡"
          actions={
            turns.length ? (
              <button
                className="btn btn-ghost btn-small"
                onClick={() => { setTurns([]); setThreadId(undefined); }}
              >
                Start a new thread
              </button>
            ) : null
          }
        >
          <div className="stack-16">
            {threads.data.threads.slice(0, 8).map((t) => (
              <button
                key={t.id}
                className={`thread-row${threadId === t.id ? ' active' : ''}`}
                onClick={() => openThread(t)}
              >
                <span className="thread-title">{t.title}</span>
                <span className="field-hint">
                  {t.document_title ?? 'Contract removed'} · {formatDate(t.created_at)}
                </span>
              </button>
            ))}
          </div>
        </Panel>
      ) : null}

      {turns.map((turn, i) => (
        <article className="answer-card" key={i}>
          <div className="answer-q">{turn.question}</div>
          {turn.error ? (
            <ErrorNote error={turn.error} />
          ) : (
            <>
              <div className="answer-text">{turn.answer!.body}</div>

              {turn.answer!.refused ? (
                <div className="note-box">
                  InBrief declined to answer rather than guess. That is deliberate — an answer with
                  no passage behind it is worse than no answer.
                </div>
              ) : null}

              {turn.answer!.citations.length > 0 ? (
                <div className="qa-source">
                  <div className="reasons-label">From the contract</div>
                  {turn.answer!.citations.map((c, j) => (
                    <blockquote className="qa-answer" key={j}>
                      <span className="cite">
                        page {c.pageNumber}{c.clauseRef ? ` · ${c.clauseRef}` : ''}
                      </span>
                      <p>{c.citedText}</p>
                    </blockquote>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </article>
      ))}
    </>
  );
}
